import React, { useMemo } from "react";
import { VB_TOKENS, VB_FONTS } from "./tokens";
import { clamp, lerp, lcg } from "./mathUtils";
import { useReducedMotion } from "./clock";

// =============================================================================
// Particles — production port of SkeletonKey.jsx (lines 290-457).
//
// Provides the two cascade/dissolve effects used at round-end:
//   - <GoldCascade/>   coins/gems/crypto pour from vault toward camera (WIN)
//   - <AshDissolve/>   gold dissolves to grey ash drifting upward (BUST)
//
// Both use deterministic LCG seeds (7 for GoldCascade, 19 for AshDissolve) so
// per-particle layouts are stable across runs. Internal SVG ids are prefixed
// `pt-` to avoid collision with SkeletonKey's `sk-` and VaultDoor's `vd-` ids.
// =============================================================================

// ----- Shared types ----------------------------------------------------------
interface PassthroughProps {
  style?: React.CSSProperties;
  className?: string;
}

// =============================================================================
// Coin — gold disc with stamped "V". Internal symbol, only used by GoldCascade.
// Source ref: SkeletonKey.jsx:347-364.
// =============================================================================
interface CoinProps {
  size: number;
}

function Coin({ size }: CoinProps) {
  const T = VB_TOKENS;
  const id = `pt-co-${size}`;
  return (
    <svg width={size} height={size} viewBox="-10 -10 20 20" style={{ display: "block" }}>
      <defs>
        <radialGradient id={id} cx="0.35" cy="0.3" r="0.7">
          <stop offset="0" stopColor={T.goldHot} />
          <stop offset="0.5" stopColor={T.gold4} />
          <stop offset="1" stopColor={T.gold1} />
        </radialGradient>
      </defs>
      <circle r="9" fill={`url(#${id})`} stroke={T.gold0} strokeWidth="0.6" />
      <circle r="6.5" fill="none" stroke={T.gold0} strokeWidth="0.5" />
      <text
        x="0"
        y="3"
        textAnchor="middle"
        fontSize="9"
        fontWeight={700}
        fill={T.gold0}
        fontFamily={VB_FONTS.display}
      >
        V
      </text>
      <ellipse cx="-3" cy="-4" rx="3" ry="1.4" fill={T.goldHot} opacity="0.7" />
    </svg>
  );
}

// =============================================================================
// Gem — cyan polygon diamond. Internal symbol, only used by GoldCascade.
// Source ref: SkeletonKey.jsx:366-382.
// =============================================================================
interface GemProps {
  size: number;
}

function Gem({ size }: GemProps) {
  const T = VB_TOKENS;
  const id = `pt-gm-${size}`;
  return (
    <svg width={size} height={size} viewBox="-10 -10 20 20" style={{ display: "block" }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={T.cyan5} />
          <stop offset="0.5" stopColor={T.cyan3} />
          <stop offset="1" stopColor={T.cyan1} />
        </linearGradient>
      </defs>
      <polygon
        points="0,-9 7,-3 4,8 -4,8 -7,-3"
        fill={`url(#${id})`}
        stroke={T.cyan0}
        strokeWidth="0.5"
      />
      <polygon points="0,-9 7,-3 0,-2 -7,-3" fill={T.cyan5} opacity="0.55" />
      <line x1="0" y1="-9" x2="0" y2="8" stroke={T.cyan5} strokeWidth="0.4" opacity="0.5" />
    </svg>
  );
}

// =============================================================================
// CryptoSymbol — gold disc with ₿ glyph. Internal symbol for GoldCascade.
// Source ref: SkeletonKey.jsx:384-399.
// =============================================================================
interface CryptoSymbolProps {
  size: number;
}

function CryptoSymbol({ size }: CryptoSymbolProps) {
  const T = VB_TOKENS;
  const id = `pt-cs-${size}`;
  return (
    <svg width={size} height={size} viewBox="-10 -10 20 20" style={{ display: "block" }}>
      <defs>
        <radialGradient id={id} cx="0.3" cy="0.3" r="0.7">
          <stop offset="0" stopColor={T.goldHot} />
          <stop offset="1" stopColor={T.gold2} />
        </radialGradient>
      </defs>
      <circle r="9" fill={`url(#${id})`} stroke={T.gold0} strokeWidth="0.6" />
      <text
        x="0"
        y="4"
        textAnchor="middle"
        fontSize="11"
        fontWeight={700}
        fill={T.gold0}
        fontFamily={VB_FONTS.display}
      >
        {"₿"}
      </text>
    </svg>
  );
}

// ----- Per-particle property pack for GoldCascade ---------------------------
interface CascadeItem {
  i: number;
  angle: number;
  delay: number;
  speed: number;
  size: number;
  kind: number;
  spin: number;
  sway: number;
}

// Stable RNG bundle for GoldCascade — seeded LCG, 7 floats per particle.
// Source ref: SkeletonKey.jsx:293-306. Seed=7 is preserved verbatim.
// `lcg(seed, n)` always returns exactly `n` floats, so the indexed reads below
// are safe under `noUncheckedIndexedAccess` with non-null assertions.
function useCascadeItems(count: number): CascadeItem[] {
  return useMemo(() => {
    const floats = lcg(7, count * 7);
    const out: CascadeItem[] = [];
    for (let i = 0; i < count; i++) {
      const base = i * 7;
      out.push({
        i,
        angle: floats[base]! * Math.PI * 2,
        delay: floats[base + 1]! * 0.5,
        speed: 0.5 + floats[base + 2]! * 0.7,
        size: 6 + floats[base + 3]! * 14,
        kind: floats[base + 4]!,
        spin: floats[base + 5]! * 720,
        sway: floats[base + 6]! * 2 - 1,
      });
    }
    return out;
  }, [count]);
}

// =============================================================================
// GoldCascade — coins/gems/crypto pour from vault center toward the camera.
//
// Source ref: SkeletonKey.jsx:290-345. Particle mix: 60% coins, 25% gems,
// 15% crypto symbols. Each particle eases outward+downward with sway/spin and
// scales up as it "approaches camera" before fading at lifetime 1.4.
// =============================================================================
export interface GoldCascadeProps extends PassthroughProps {
  progress: number;
  t: number;
  centerX: number;
  centerY: number;
  count?: number;
  intensity?: number;
}

export function GoldCascade({
  progress,
  t,
  centerX,
  centerY,
  count = 60,
  intensity = 1,
  style,
  className,
}: GoldCascadeProps) {
  const reduced = useReducedMotion();
  const T = VB_TOKENS;
  const items = useCascadeItems(count);

  if (progress <= 0) return null;

  // Reduced-motion fallback: render a static fanned-out gold burst at terminal
  // positions, no animation, no opacity ramp.
  if (reduced) {
    return (
      <div
        className={className}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          ...style,
        }}
      >
        {items.map((it) => {
          const angle = it.angle;
          const dist = 220;
          const x = Math.cos(angle) * dist;
          const y = Math.sin(angle) * dist * 0.7;
          return (
            <div
              key={it.i}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(${centerX + x}px, ${centerY + y}px)`,
                opacity: 0.55 * intensity,
                filter: `drop-shadow(0 0 4px ${T.gold3})`,
              }}
            >
              <div
                style={{
                  width: it.size,
                  height: it.size,
                  borderRadius: "50%",
                  background: T.gold4,
                  boxShadow: `0 0 ${4 + it.size * 0.3}px ${T.gold3}`,
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        ...style,
      }}
    >
      {items.map((it) => {
        // Per-particle local time. Source: JSX:315.
        const local = clamp((progress - it.delay) * it.speed, 0, 2);
        if (local <= 0) return null;

        // Polar outward + gravity drop + horizontal sway. Source: JSX:318-325.
        const angle = it.angle;
        const dist = local * 600;
        const gravity = local * local * 220;
        const sway = Math.sin(t * 2 + it.i) * it.sway * 30;
        const x = Math.cos(angle) * dist + sway;
        const y = Math.sin(angle) * dist * 0.7 + gravity - 60;
        const scale = 1 + local * 1.2; // "approach camera" scale-up
        const rot = it.spin * local;
        const opacity = clamp(1 - (local - 1.4) * 2, 0, 1) * intensity;

        // Symbol mix: 60% coin, 25% gem, 15% crypto. Source: JSX:327-328.
        const isCoin = it.kind < 0.6;
        const isGem = it.kind >= 0.6 && it.kind < 0.85;

        return (
          <div
            key={it.i}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: `translate(${centerX + x}px, ${centerY + y}px) scale(${scale}) rotate(${rot}deg)`,
              opacity,
              filter: `drop-shadow(0 0 ${4 * scale}px ${T.gold3})`,
            }}
          >
            {isCoin && <Coin size={it.size} />}
            {isGem && <Gem size={it.size} />}
            {!isCoin && !isGem && <CryptoSymbol size={it.size} />}
          </div>
        );
      })}
    </div>
  );
}

// ----- Per-particle property pack for AshDissolve ---------------------------
interface AshItem {
  i: number;
  angle: number;
  delay: number;
  speed: number;
  size: number;
  sway: number;
  lifetime: number;
}

// Stable RNG bundle for AshDissolve — seeded LCG, 6 floats per particle.
// Source ref: SkeletonKey.jsx:405-417. Seed=19 is preserved verbatim.
// `lcg(seed, n)` always returns exactly `n` floats, so the indexed reads below
// are safe under `noUncheckedIndexedAccess` with non-null assertions.
function useAshItems(count: number): AshItem[] {
  return useMemo(() => {
    const floats = lcg(19, count * 6);
    const out: AshItem[] = [];
    for (let i = 0; i < count; i++) {
      const base = i * 6;
      out.push({
        i,
        angle: floats[base]! * Math.PI * 2,
        delay: floats[base + 1]! * 0.6,
        speed: 0.4 + floats[base + 2]! * 0.6,
        size: 2 + floats[base + 3]! * 4,
        sway: floats[base + 4]! * 2 - 1,
        lifetime: 0.7 + floats[base + 5]! * 0.5,
      });
    }
    return out;
  }, [count]);
}

// =============================================================================
// AshDissolve — gold particles desaturate to ash and drift up + back (BUST).
//
// Source ref: SkeletonKey.jsx:402-457. Color shifts from warm gold (245,200,
// 120) to cold gray (80,80,90) via colorMix = clamp(local*0.9). Parent uses
// `mix-blend-mode: screen` so particles add light against the dimmed scene.
// =============================================================================
export interface AshDissolveProps extends PassthroughProps {
  progress: number;
  t: number;
  centerX: number;
  centerY: number;
  count?: number;
  intensity?: number;
}

export function AshDissolve({
  progress,
  t,
  centerX,
  centerY,
  count = 80,
  intensity = 1,
  style,
  className,
}: AshDissolveProps) {
  const reduced = useReducedMotion();
  const items = useAshItems(count);

  if (progress <= 0) return null;

  // Reduced-motion fallback: a static dim haze, no animation.
  if (reduced) {
    return (
      <div
        className={className}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          mixBlendMode: "screen",
          ...style,
        }}
      >
        {items.map((it) => {
          const angle = it.angle;
          const dist = 140;
          const x = Math.cos(angle) * dist;
          const y = Math.sin(angle) * dist * 0.8 - 40;
          return (
            <div
              key={it.i}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(${centerX + x}px, ${centerY + y}px)`,
                width: it.size,
                height: it.size,
                borderRadius: "50%",
                background: "rgba(140,140,140,0.4)",
                filter: "blur(2px)",
                opacity: 0.5 * intensity,
              }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        mixBlendMode: "screen",
        ...style,
      }}
    >
      {items.map((it) => {
        // Per-particle local time. Source: JSX:427.
        const local = clamp((progress - it.delay) * it.speed, 0, 2);
        if (local <= 0) return null;

        // Polar outward + UPWARD drift (negative y). Source: JSX:430-434.
        const angle = it.angle;
        const dist = local * 380;
        const sway = Math.sin(t * 1.5 + it.i * 0.7) * it.sway * 20;
        const x = Math.cos(angle) * dist + sway;
        const y = Math.sin(angle) * dist * 0.8 - local * 60;
        const scale = 1 + local * 1.6;

        // Color desat curve: warm gold → cold gray. Source: JSX:437-440.
        const colorMix = clamp(local * 0.9, 0, 1);
        const r = lerp(245, 80, colorMix);
        const g = lerp(200, 80, colorMix);
        const b = lerp(120, 90, colorMix);
        const opacity = clamp(1 - (local - it.lifetime) * 3, 0, 1) * intensity;

        const ri = r | 0;
        const gi = g | 0;
        const bi = b | 0;

        return (
          <div
            key={it.i}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: `translate(${centerX + x}px, ${centerY + y}px) scale(${scale})`,
              width: it.size,
              height: it.size,
              borderRadius: "50%",
              background: `rgba(${ri},${gi},${bi},${opacity})`,
              filter: `blur(${1 + local * 2}px)`,
              boxShadow: `0 0 ${6 + local * 4}px rgba(${ri},${gi},${bi},${opacity * 0.5})`,
            }}
          />
        );
      })}
    </div>
  );
}
